import { Component, Output, EventEmitter, Input, OnInit, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProductsSupplierSearchDataService } from 'src/app/_services';
import { asArray, SupplierProductAssortmentStatus, IEnumValue, ProductField, LxmAppModule, ProductPriceSchemeType, asObject, UserAction } from 'src/app/enum';
import { AppState } from 'src/app/state/app.state';
import { IProductsSearchFormData, ICountryTranslated, ISegmentTreeOption, IClvDto, IRetailChain } from 'src/app/cards/product/product-info/product-info.model';
import { ActivatedRoute } from '@angular/router';
import { LocalStorage } from 'src/app/_helpers';
import { ProductsListState } from 'src/app/cards/products/products-list.state';

@Component({
  selector: 'product-search-supplier-card',
  templateUrl: './product-search-supplier.html',
  host: { 'class': 'product-search-card' },
  styleUrls: ['./product-search-supplier.scss']
})
export class SupplierProductSearchCard implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  public LxmAppModule = LxmAppModule;

  public formData: IProductsSearchFormData;

  @Output()
  public search = new EventEmitter();

  @Input()
  public isOpen = false;

  @Input()
  private localStorageKey: string;

  public statuses: IEnumValue[];

  public countryOptions: ICountryTranslated[];
  public nutritionalClaimsOptions: IEnumValue[];
  public segmentOptions: ISegmentTreeOption[];
  public tagsOptions: IClvDto[];
  public allergenOptions: IClvDto[];
  public productPriceSchemeType = ProductPriceSchemeType;

  public schemeTypeOptions: IEnumValue[];

  public clientsOptions: IRetailChain[];
  public reailersOptions: IRetailChain[];

  public storeCategoryOptions: { id: string; name: string }[];

  public searchOperatorOptions = [
    { name: 'enums.AndOr.And', id: 1 },
    { name: 'enums.AndOr.Or', id: 2 }
  ];

  public searchProductValidationOptions = [
    { name: 'enums.ProductValidation.NoPictures', id: 1 },
    { name: 'enums.ProductValidation.ProductDataInvalidValues', id: 2 }
  ];

  public searchProductConformityOptions = [
    { name: 'enums.SearchProductConformity.Confirmed', id: 1 },
    { name: 'enums.SearchProductConformity.Unconfirmed', id: 2 },
    { name: 'enums.SearchProductConformity.SentForConfirmation', id: 3 }
  ];

  public searchAllergenContentOptions = [
    { name: 'enums.ProductAllergenContentType.MayContain', id: 1 },
    { name: 'enums.ProductAllergenContentType.Contains', id: 2 },
    { name: 'enums.ProductAllergenContentType.DoesNotContain', id: -1 }
  ];

  public isVisibleInStoreOptions = [
    { name: 'global.yes', id: true },
    { name: 'global.no', id: false }
  ];

  public showProductConformityStateSearch = false;
  public showStoreSearch = false;

  constructor(
    private _state: ProductsListState,
    public appState: AppState,
    private _productsSearchDataService: ProductsSupplierSearchDataService,
    route: ActivatedRoute) {
    this.formData = (route.snapshot.data.formData || route.snapshot.data.productCard.formData) as IProductsSearchFormData;

    this.countryOptions = this.formData.countries;
    this.tagsOptions = this.formData.productTags;
    this.clientsOptions = this.formData.clients;
    this.reailersOptions = this.formData.clients;

    this.statuses = asArray(SupplierProductAssortmentStatus);
    this.nutritionalClaimsOptions = asArray(ProductField).filter(x => this.formData.nutritionalClaims?.includes(x.id));
    this.segmentOptions = this.formData.segmentTreeOptions;
    this.allergenOptions = this.formData.allergens;
    this.storeCategoryOptions = this.formData.storeCategories;

    this.showProductConformityStateSearch =
      appState.hasModule(LxmAppModule.ConformityDeclarations) &&
      appState.hasRight([UserAction.ViewConformityDeclarations]);

    this.showStoreSearch =
      appState.hasModule(LxmAppModule.SellerStore) &&
      appState.hasRight([UserAction.ViewStoreProducts]);

    this.schemeTypeOptions = [
      asObject(ProductPriceSchemeType, ProductPriceSchemeType.Main),
      asObject(ProductPriceSchemeType, ProductPriceSchemeType.Campaign),
      asObject(ProductPriceSchemeType, ProductPriceSchemeType.Procurement),
      ...(this.showStoreSearch ? [asObject(ProductPriceSchemeType, ProductPriceSchemeType.Store)] : [])
    ];
  }

  public get searchData(): FormGroup {
    return this._productsSearchDataService.searchData;
  }

  public get showAllergensFilter(): boolean {
    return this._productsSearchDataService.searchData.get("allergenContentType").value;
  }

  public get productPriceSchemeFilterSelected(): boolean {
    return this._productsSearchDataService.productPriceSchemeFilterSelected;
  }

  public get showRetailersForProductValidation(): boolean {
    return this._productsSearchDataService.showRetailersForProductValidation;
  }

  public get isLoading() {
    return this._state.isSearchLoading;
  }

  public toggleOpen(isOpen: boolean) {
    this.isOpen = isOpen;
    LocalStorage.set(this.localStorageKey, isOpen);
  }

  public segmentTreeLabel(i: ISegmentTreeOption) {
    return `${'--'.repeat(i.level)} ${i.code} ${i.name}`.trim();
  }

  public disableOption(i: ISegmentTreeOption) {
    return !i.canAssignToProduct;
  }

  public ngOnInit(): void {
    this._syncIsVisibleInStoreFilter();
    this.submit();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _syncIsVisibleInStoreFilter(): void {
    const searchData = this._productsSearchDataService.searchData;
    const enabledControl = searchData.get('isVisibleInStoreFilterEnabled');
    const valueControl = searchData.get('isVisibleInStore');

    if (valueControl.value != null) {
      enabledControl.setValue(true, { emitEvent: false });
    }

    enabledControl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((enabled) => {
      if (!enabled) {
        valueControl.setValue(null, { emitEvent: false });
      } else if (valueControl.value == null) {
        valueControl.setValue(true, { emitEvent: false });
      }
    });
  }

  public submit() {
    const val = this._productsSearchDataService.serializeSearchData();
    this.search.emit(val);
  }

}
